import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Compass, Users, User, LogOut, Languages, Bell, Sparkles, CalendarDays, Shield, MessageCircle } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { LANGUAGES, api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import Avatar from "@/components/Avatar";

export default function NavBar() {
  const { t, lang, setLang } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [dmUnread, setDmUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      try {
        const [nRes, dRes] = await Promise.all([
          api.get("/notifications/unread-count"),
          api.get("/messages/unread-count"),
        ]);
        setUnread(nRes.data.count || 0);
        setDmUnread(dRes.data.count || 0);
      } catch {}
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 10000);
    return () => clearInterval(id);
  }, [user]);

  const tabs = [
    { to: "/discover", label: t("discover"), icon: Compass, testid: "nav-discover" },
    { to: "/events", label: t("events"), icon: Sparkles, testid: "nav-events" },
    { to: "/groups", label: t("groups"), icon: Users, testid: "nav-groups" },
    { to: "/messages", label: t("messages"), icon: MessageCircle, testid: "nav-messages", badge: dmUnread },
    { to: "/calendar", label: t("calendar"), icon: CalendarDays, testid: "nav-calendar" },
    { to: "/notifications", label: t("notifications"), icon: Bell, testid: "nav-notifications", badge: unread },
  ];

  return (
    <header className="sticky top-0 z-30 glass" data-testid="app-navbar">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
        <Link to={user ? "/discover" : "/"} className="flex items-center gap-2 group" data-testid="nav-logo">
          <img
            src="/app-icon-192.png"
            alt="SocialHub"
            width={36}
            height={36}
            className="h-9 w-9 rounded-2xl object-cover shadow-[0_4px_18px_rgba(22,200,90,0.25)] group-hover:-translate-y-0.5 transition-transform"
          />
          <span className="font-heading text-xl font-semibold tracking-tight">{t("appName")}</span>
        </Link>

        {user && (
          <nav className="hidden md:flex items-center gap-1 ms-2">
            {tabs.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                data-testid={tab.testid}
                className={({ isActive }) =>
                  `relative flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all ${
                    isActive
                      ? "bg-[hsl(var(--primary))] text-white shadow-sm"
                      : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]"
                  }`
                }
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
                {tab.badge > 0 && (
                  <span className="ms-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full text-[10px] font-bold bg-[hsl(var(--primary))] text-white border-2 border-[hsl(var(--background))] -translate-y-0.5" data-testid={`${tab.testid}-badge`}>
                    {tab.badge > 9 ? "9+" : tab.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
        )}

        <div className="ms-auto flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="rounded-full gap-1.5" data-testid="lang-switcher">
                <Languages className="h-4 w-4" />
                <span className="text-xs uppercase tracking-wider">{lang}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl">
              <DropdownMenuLabel className="text-xs">{t("language")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {LANGUAGES.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  data-testid={`lang-${l.code}`}
                  className={lang === l.code ? "bg-[hsl(var(--muted))]" : ""}
                >
                  <span className="me-2 text-xs uppercase opacity-60">{l.code}</span>
                  {l.native}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="rounded-full gap-2 ps-1" data-testid="user-menu">
                  <Avatar user={user} size={28} />
                  <span className="hidden sm:inline">{user.alias}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl w-48">
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <User className="h-4 w-4 me-2" /> {t("profile")}
                </DropdownMenuItem>
                {user?.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <Shield className="h-4 w-4 me-2" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { navigate("/", { replace: true }); logout(); }} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 me-2" /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" size="sm" className="rounded-full" onClick={() => navigate("/login")} data-testid="navbar-login-btn">
                {t("login")}
              </Button>
              <Button size="sm" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white" onClick={() => navigate("/register")} data-testid="navbar-register-btn">
                {t("getStarted")}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Mobile tabs */}
      {user && (
        <nav className="md:hidden border-t border-[hsl(var(--border))] flex">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              data-testid={`m-${tab.testid}`}
              className={({ isActive }) =>
                `relative flex-1 flex flex-col items-center gap-0.5 py-2 text-[11px] font-medium ${
                  isActive ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))]"
                }`
              }
            >
              <div className="relative">
                <tab.icon className="h-4 w-4" />
                {tab.badge > 0 && (
                  <span className="absolute -top-1.5 -end-2 min-w-[16px] h-4 px-1 inline-flex items-center justify-center rounded-full text-[9px] font-bold bg-[hsl(var(--primary))] text-white">
                    {tab.badge > 9 ? "9+" : tab.badge}
                  </span>
                )}
              </div>
              {tab.label}
            </NavLink>
          ))}
        </nav>
      )}
    </header>
  );
}
